import React, { useState } from 'react';
import {
  Link2,
  Lock,
  Copy,
  Check,
  AlertTriangle,
} from 'lucide-react';

import {
  Alert,
  Box,
  Button,
  Code,
  Container,
  CopyButton,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';

import useUser from './userProvider.jsx';
import useSubscription from './subscriptionProvider.js';

const ZK_SUBSCRIPTIONS = ['ZK', 'Admin'];

export default function ZkTimestampPage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { userInfo } = useUser();
  const { data: subscription, isLoading: subscriptionLoading, error: subscriptionError } = useSubscription(userInfo?.sub);

  const [repo, setRepo] = useState('');
  const [target, setTarget] = useState('');
  const [postUrl, setPostUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  if (userInfo == null) {
    const url = new URL('/api/login', document.location);
    const params = new URLSearchParams({ cont: document.location });
    url.search = params.toString();
    document.location = url.toString();
    return null;
  }

  if (subscriptionLoading) {
    return (
      <Box mih="40vh" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader />
      </Box>
    );
  }

  const subscriptionKnown = subscription != null && !subscriptionError;
  const isZkAdmin = subscriptionKnown && ZK_SUBSCRIPTIONS.includes(subscription);

  if (subscriptionKnown && !isZkAdmin) {
    return (
      <Box mih="100vh" py={60}>
        <Container size="sm">
          <Paper withBorder radius="xl" p="xl">
            <Stack align="center" ta="center" gap="sm">
              <ThemeIcon variant="light" color="red" size={56} radius="xl">
                <Lock size={26} />
              </ThemeIcon>
              <Title order={1} fz="1.75rem" fw={800}>
                {t('This feature requires a ZK or Admin subscription.')}
              </Title>
              <Text c="dimmed">
                {t('The Zero-Knowledge commit API is available with ZK and Admin subscriptions.')}
              </Text>
              <Button variant="light" color="teal" onClick={() => setLocation('/pricing')}>
                {t('View Pricing')}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  const generateUrl = async (event) => {
    event.preventDefault();
    setError(null);
    setPostUrl(null);

    if (!repo.trim() || !target.trim()) {
      setError({ message: t('Repository and target repository are required.') });
      return;
    }

    setGenerating(true);
    try {
      const rsp = await fetch(`/api/timestamp/${encodeURIComponent(userInfo.sub)}/${encodeURIComponent(repo.trim())}/${encodeURIComponent(target.trim())}`);
      if (!rsp.ok) {
        const info = await rsp.text().catch(() => null);
        const err = new Error(info || `HTTP ${rsp.status}`);
        err.status = rsp.status;
        throw err;
      }
      const data = await rsp.json();
      setPostUrl(data.url);
    } catch (err) {
      setError({
        status: err.status,
        message: err.status === 403
          ? t('Your subscription does not allow generating this URL.')
          : t('Could not generate the URL. Please try again.'),
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="md">
        <Stack gap="lg">
          <Paper withBorder radius="md" p="md">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <ThemeIcon size={42} radius="md" variant="light" color="teal">
                <Link2 size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Title order={1} fz="2.25rem" fw={800}>
                  {t('ZK Timestamp URL')}
                </Title>
                <Text c="dimmed" size="lg">
                  {t('Generate the authenticated POST URL that your CI/CD pipeline can use to timestamp commits without exposing your source code.')}
                </Text>
              </Stack>
            </Group>
          </Paper>

          <Paper withBorder radius="md" p="lg">
            <form onSubmit={generateUrl}>
              <Stack gap="md">
                <TextInput
                  label={t('Owner')}
                  value={userInfo.sub}
                  disabled
                />
                <TextInput
                  label={t('Repository')}
                  placeholder="my-project"
                  value={repo}
                  onChange={(event) => setRepo(event.currentTarget.value)}
                  required
                />
                <TextInput
                  label={t('Target Repository')}
                  placeholder="my-project-proofs"
                  value={target}
                  onChange={(event) => setTarget(event.currentTarget.value)}
                  required
                />
                {error && (
                  <Alert color="red" variant="light" icon={<AlertTriangle size={16} />}>
                    {error.message}
                  </Alert>
                )}
                <Group justify="flex-end">
                  <Button type="submit" color="teal" loading={generating}>
                    {t('Generate URL')}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>

          {postUrl && (
            <Paper withBorder radius="md" p="lg">
              <Stack gap="sm">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="teal" size="md" radius="xl">
                    <Check size={14} />
                  </ThemeIcon>
                  <Text fw={700}>{t('POST URL')}</Text>
                </Group>
                <Code block maw={1200} style={{ overflowWrap: 'anywhere' }}>
                  {postUrl}
                </Code>
                <Text size="sm" c="dimmed">
                  {t('Append the commit SHA to this URL to timestamp a commit.')}
                </Text>
                <Text size="sm" c="dimmed">
                  {t('Example:')}{' '}
                  <Code>curl -X POST "{postUrl}{'{commitSha}'}"</Code>
                </Text>
                <Group justify="flex-end">
                  <CopyButton value={postUrl} timeout={2000}>
                    {({ copied, copy }) => (
                      <Button variant="light" color={copied ? 'teal' : 'blue'} onClick={copy} leftSection={copied ? <Check size={16} /> : <Copy size={16} />}>
                        {copied ? t('Copied') : t('Copy URL')}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
